<script setup>
import { ref, watch, computed, onMounted } from "vue";
import AutoComplete from "primevue/autocomplete";
import { useField } from "vee-validate";
import * as yup from "yup";

const props = defineProps({
  name: String,
  label: String,
  modelValue: [String, Number], // Added for v-model support
  placeholder: String,
  description: String,
  numbersOnly: Boolean,
  max: Number,
  min: Number,
  schema: Object,
  showRequired: {
    type: Boolean,
    default: true, // Show required asterisk by default
  },
  required: Boolean,
  type: {
    type: String,
    default: "text",
  },
});

const emit = defineEmits(["update:modelValue"]); // added for v-model support
const field = props.name;

// Define a computed property for the validation rules.
// This ensures that if props.schema is reactive (e.g., a computed from parent)
// or if other props influencing the internal schema change,
// useField will always get the up-to-date rules.
const validationRules = computed(() => {
  if (props.schema) {
    // If a schema is provided via props, use it directly.
    // This is expected to be a Yup schema object or a reactive ref to one (like a computed property).
    return props.schema;
  }

  // Build internal schema if props.schema is not provided
  let internalSchema = yup.string();

  // Apply general max length
  // Yup's default for max is no limit if props.max is undefined.
  internalSchema = internalSchema.max(
    props.max || Infinity,
    props.max ? `${props.label} is too long.` : ""
  );

  // Apply general min length. This will interact with password's specific min.
  // Yup will enforce the stricter (larger) min if multiple .min() are chained.
  internalSchema = internalSchema.min(
    props.min || 0,
    props.min ? `${props.label} is too short.` : ""
  );

  if (props.numbersOnly) {
    internalSchema = internalSchema.matches(
      /^\d+$/,
      `${props.label} must contain numbers only`
    );
  }

  // Type-specific validations
  if (props.type === "email") {
    internalSchema = internalSchema.matches(
      /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
      `${props.label || "Email"} is invalid`
    );
  } else if (props.type === "password") {
    internalSchema = internalSchema
      .min(
        8,
        `${props.label || "Password"} should be at least 8 characters long`
      )
      .matches(
        /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]).*$/,
        `${
          props.label || "Password"
        } must contain a mix of letters, numbers, and special characters`
      );
  }

  // Apply required validation last
  if (props.required) {
    internalSchema = internalSchema.required(`${props.label} is required.`);
  }
  return internalSchema;
});

// Vee-validate field setup
const { value, errorMessage, validate, setValue, meta } = useField(
  props.name,
  validationRules, // Pass the computed validation rules to useField
  {
    initialValue: props.modelValue, // Use modelValue for initial value
  }
);
// Expose the validate function and meta object so parent components can interact with the field's validation
defineExpose({ validate, meta });
// Watch for changes in the internal value and emit update:modelValue
watch(value, (newValue) => {
  emit("update:modelValue", newValue);
});

// Watch for changes in the modelValue prop (if changed from parent)
watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue !== value.value) {
      setValue(newModelValue); // Update internal value if prop changes
    }
  }
);
</script>

<template>
  <fieldset class="frm-field">
    <label :for="name" class="block mb-10">
      {{ label }}
      <span v-if="required && showRequired" class="text-red-500">*</span>
    </label>
    <div class="frm-input">
      <input
        :type="type"
        :id="name"
        :name="name"
        :placeholder="placeholder"
        v-model="value"
        @blur="validate"
        class="w-full" />
    </div>
    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none">
      {{ errorMessage }}
    </span>

    <span v-if="description" class="text-13 frm-desc">
      {{ description }}
    </span>
  </fieldset>
</template>
